<template>
  <div class="risk-page">
    <page-header title="风险中心" description="集中处理 AI 识别出的中高风险内容，跟进证据和处理进展" />

    <div class="summary-strip">
      <div class="summary-item">
        <span>待处理</span>
        <strong>{{ pendingCount }}</strong>
      </div>
      <div class="summary-item high">
        <span>高风险</span>
        <strong>{{ levelCount('高') }}</strong>
      </div>
      <div class="summary-item medium">
        <span>中风险</span>
        <strong>{{ levelCount('中') }}</strong>
      </div>
      <div class="summary-note">
        风险内容来自 AI 语义分析，关闭或标记误报后仍保留处理记录
      </div>
    </div>

    <el-card v-loading="loading" shadow="never">
      <div class="toolbar">
        <div class="filters">
          <el-select v-model="level" clearable placeholder="全部风险等级" @change="resetPage">
            <el-option label="高风险" value="高" />
            <el-option label="中风险" value="中" />
            <el-option label="低风险" value="低" />
          </el-select>
          <el-select v-model="status" clearable placeholder="全部处理状态" @change="resetPage">
            <el-option v-for="value in statuses" :key="value" :label="value" :value="value" />
          </el-select>
          <el-input
            v-model.trim="keyword"
            class="keyword-input"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索内容、平台或负责人"
            @input="resetPage"
          />
          <el-button v-if="hasFilter" type="text" @click="resetFilters">清空筛选</el-button>
        </div>
        <el-button
          icon="el-icon-download"
          :disabled="filtered.length === 0"
          :loading="exporting"
          @click="download"
        >
          导出当前结果（{{ filtered.length }} 条）
        </el-button>
        <el-button
          v-if="legacyCandidates.length > 0"
          type="warning"
          plain
          :loading="legacyCleaning"
          @click="legacyCleanupVisible = true"
        >
          清理旧版低价值记录（{{ legacyCandidates.length }} 条）
        </el-button>
      </div>

      <risk-table :items="pagedItems" @open="open" />

      <div v-if="filtered.length > pageSize" class="pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page.sync="currentPage"
          :page-size="pageSize"
          :total="filtered.length"
        />
      </div>
    </el-card>

    <risk-process-drawer :visible.sync="drawer" :risk="selected" @saved="load" />

    <el-dialog title="清理旧版低价值记录" :visible.sync="legacyCleanupVisible" width="480px">
      <p>将清理 {{ legacyCandidates.length }} 条已关闭的旧版低风险记录。</p>
      <p>仅清理“只命中品牌、未命中异常词”的历史数据，同时删除其残留证据、正文和评论；不会影响待处理或 AI 确认的风险。</p>
      <span slot="footer">
        <el-button @click="legacyCleanupVisible = false">取消</el-button>
        <el-button type="danger" :loading="legacyCleaning" @click="cleanupLegacy">确认清理</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'
import PageHeader from '../components/PageHeader.vue'
import RiskTable from '../components/RiskTable.vue'
import RiskProcessDrawer from './RiskProcessDrawer.vue'

export default {
  components: { PageHeader, RiskTable, RiskProcessDrawer },
  data () {
    return {
      items: [],
      loading: false,
      level: '',
      status: '',
      keyword: '',
      statuses: ['待确认', '处理中', '已关闭', '误报'],
      currentPage: 1,
      pageSize: 20,
      drawer: false,
      selected: null,
      exporting: false,
      legacyCandidates: [],
      legacyCleanupVisible: false,
      legacyCleaning: false
    }
  },
  computed: {
    filtered () {
      const keyword = this.keyword.toLowerCase()
      return this.items.filter(item => {
        const text = [item.title, item.risk_summary, item.platform, item.assignee]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return (!this.level || item.risk_level === this.level) &&
          (!this.status || item.process_status === this.status) &&
          (!keyword || text.indexOf(keyword) >= 0)
      })
    },
    pagedItems () {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filtered.slice(start, start + this.pageSize)
    },
    pendingCount () {
      return this.items.filter(item => ['已关闭', '误报'].indexOf(item.process_status) < 0).length
    },
    hasFilter () {
      return Boolean(this.level || this.status || this.keyword)
    }
  },
  mounted () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      try {
        const [risks, legacy] = await Promise.all([
          yuqingApi.get('risk-records'),
          yuqingApi.get('risk-records/legacy-low-value')
        ])
        this.items = risks.risks || []
        this.legacyCandidates = legacy.risks || []
        this.resetPage()
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.loading = false
      }
    },
    levelCount (level) {
      return this.items.filter(item => item.risk_level === level).length
    },
    resetPage () {
      this.currentPage = 1
    },
    resetFilters () {
      this.level = ''
      this.status = ''
      this.keyword = ''
      this.resetPage()
    },
    open (risk) {
      this.selected = risk
      this.drawer = true
    },
    download () {
      if (this.exporting || this.filtered.length === 0) return
      this.exporting = true
      const params = []
      if (this.level) params.push(`riskLevel=${encodeURIComponent(this.level)}`)
      if (this.status) params.push(`processStatus=${encodeURIComponent(this.status)}`)
      if (this.keyword) params.push(`keyword=${encodeURIComponent(this.keyword)}`)
      yuqingApi.download(`risk-records.xlsx${params.length ? `?${params.join('&')}` : ''}`)
      this.$message.success(`正在导出 ${this.filtered.length} 条风险记录`)
      window.setTimeout(() => { this.exporting = false }, 800)
    },
    async cleanupLegacy () {
      if (this.legacyCleaning) return
      this.legacyCleaning = true
      try {
        const result = await yuqingApi.post('risk-records/legacy-low-value/cleanup', { confirm: true })
        this.$message.success(`已清理 ${result.removed || 0} 条旧版低价值记录`)
        this.legacyCleanupVisible = false
        await this.load()
      } catch (error) {
        this.$message.error(error.message || '清理失败，请稍后重试')
      } finally {
        this.legacyCleaning = false
      }
    }
  }
}
</script>

<style scoped>
.summary-strip {
  display: flex;
  min-height: 68px;
  align-items: stretch;
  margin-bottom: 14px;
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}
.summary-item {
  display: flex;
  min-width: 140px;
  align-items: baseline;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 22px;
  border-right: 1px solid #ebeef5;
  color: #606266;
}
.summary-item strong {
  color: #303133;
  font-size: 24px;
  line-height: 1;
}
.summary-item.high strong {
  color: #f56c6c;
}
.summary-item.medium strong {
  color: #e6a23c;
}
.summary-note {
  display: flex;
  align-items: center;
  padding: 0 22px;
  color: #909399;
  font-size: 13px;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
}
.filters .el-select {
  width: 165px;
}
.keyword-input {
  width: 240px;
}
.pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 18px;
}
@media (max-width: 1100px) {
  .summary-note {
    display: none;
  }
  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }
  .filters {
    width: 100%;
    flex-wrap: wrap;
  }
}
</style>
